import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDividerModule } from '@angular/material/divider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DisplayTemplateService } from '../../../api/gen/api/displayTemplate.service';
import { AddImageToSlotRequest, AddImageToLayerRequest, DisplayWithHealthDto, DisplayTemplateDto, DisplaySlotDto, LayerDefinitionDto } from '../../../api/gen';
import { DisplayState } from '../../../api/gen/model/displayState';
import { DisplaySlotType } from '../../../api/gen/model/displaySlotType';
import { LayoutMode } from '../../../api/gen/model/layoutMode';
import { LayoutStyleType } from '../../../api/gen/model/layoutStyleType';
import { ContainerStyleConfig } from '../../../api/gen/model/containerStyleConfig';
import { CreateTemplateItemComponent } from './create-template-item/create-template-item.component';
import { EditTemplateItemComponent } from './edit-template-item/edit-template-item.component';
import { CloneTemplateModalComponent } from './clone-template-modal/clone-template-modal.component';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
    selector: 'app-display-template-editor',
    standalone: true,
    imports: [
        MatButtonModule, MatIconModule, MatProgressSpinnerModule, MatDividerModule, MatTooltipModule,
        CreateTemplateItemComponent, EditTemplateItemComponent, CloneTemplateModalComponent
    ],
    templateUrl: './display-template-editor.component.html',
    styleUrl: './display-template-editor.component.scss'
})
export class DisplayTemplateEditorComponent implements OnInit, OnChanges {
    errorMessage = signal<string | null>(null);
    private readonly templateService = inject(DisplayTemplateService);
    private readonly snackBar = inject(MatSnackBar);

    @Input() display!: DisplayWithHealthDto;
    @Input() siteId: string | null = null;
    @Output() close = new EventEmitter<void>();

    loading = signal(false);
    saving = signal(false);
    showWorkflowBanner = signal(true);
    showCloneModal = signal(false);
    template = signal<DisplayTemplateDto | null>(null);
    selectedState = signal<DisplayState | null>(null);

    ngOnInit(): void {
        this.loadTemplate();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['display'] && !changes['display'].firstChange) {
            this.reset();
            this.loadTemplate();
        }
    }

    private reset(): void {
        this.template.set(null);
        this.selectedState.set(null);
        this.showWorkflowBanner.set(false);
    }

    async loadTemplate(): Promise<void> {
        if (!this.display?.displayId) return;
        this.loading.set(true);
        try {
            const existingId = this.template()?.id;
            let t: DisplayTemplateDto | null = null;

            if (existingId) {
                t = await firstValueFrom(this.templateService.displayTemplateIdGet(existingId));
            } else {
                const result = await firstValueFrom(
                    this.templateService.displayTemplateGet(undefined, this.display.displayId)
                );
                const found = result?.items?.[0] ?? null;
                if (found?.id) {
                    t = await firstValueFrom(this.templateService.displayTemplateIdGet(found.id));
                }
            }

            this.template.set(t);
        } catch {
            this.snackBar.open('Failed to load template', 'Close', { duration: 3000 });
        } finally {
            this.loading.set(false);
        }
    }

    async createTemplate(data: { name: string; layoutMode: LayoutMode }): Promise<void> {
        if (!this.display.displayId) return;
        this.saving.set(true);
        try {
            const created = await firstValueFrom(
                this.templateService.displayTemplatePost({
                    displayId: this.display.displayId,
                    name: data.name,
                    parkingSpaceId: this.siteId ?? undefined,
                    layoutMode: data.layoutMode
                })
            );
            this.template.set(created);
            this.snackBar.open('Template created', 'Close', { duration: 2000 });
        } catch {
            this.snackBar.open('Failed to create template', 'Close', { duration: 3000 });
        } finally {
            this.saving.set(false);
        }
    }

    async saveTemplateName(name: string): Promise<void> {
        const t = this.template();
        if (!t?.id || !name) return;
        this.saving.set(true);
        try {
            const updated = await firstValueFrom(
                this.templateService.displayTemplateIdPut(t.id, { name })
            );
            this.template.set(updated);
            this.snackBar.open('Template name saved', 'Close', { duration: 2000 });
        } catch {
            this.snackBar.open('Failed to save template name', 'Close', { duration: 3000 });
        } finally {
            this.saving.set(false);
        }
    }

    selectState(state: DisplayState): void {
        this.selectedState.update(current => current === state ? null : state);
    }

    async removeStateLayout(state: DisplayState): Promise<void> {
        const t = this.template();
        if (!t?.id) return;
        this.saving.set(true);
        try {
            await firstValueFrom(
                this.templateService.displayTemplateIdStateLayoutDelete(t.id, state)
            );
            const reloaded = await firstValueFrom(this.templateService.displayTemplateIdGet(t.id));
            this.template.set(reloaded);
            if (this.selectedState() === state) this.selectedState.set(null);
            this.snackBar.open('State layout cleared', 'Close', { duration: 2000 });
        } catch {
            this.snackBar.open('Failed to clear state layout', 'Close', { duration: 3000 });
        } finally {
            this.saving.set(false);
        }
    }

    async cloneState(event: { source: DisplayState; target: DisplayState }): Promise<void> {
        const t = this.template();
        if (!t?.id) return;
        this.saving.set(true);
        try {
            await firstValueFrom(
                this.templateService.displayTemplateIdStateLayoutClonePost(t.id, {
                    sourceState: event.source,
                    targetState: event.target
                })
            );
            const reloaded = await firstValueFrom(this.templateService.displayTemplateIdGet(t.id));
            this.template.set(reloaded);
            this.selectedState.set(event.target);
            this.snackBar.open(`Copied ${event.source} to ${event.target}`, 'Close', { duration: 2000 });
        } catch {
            this.snackBar.open('Failed to copy state', 'Close', { duration: 3000 });
        } finally {
            this.saving.set(false);
        }
    }

    async saveStateLayout(event: { state: DisplayState; slots: Partial<DisplaySlotDto>[]; layers?: Partial<LayerDefinitionDto>[]; pendingImages: Map<string, File>; pendingLayerImages?: Map<string, File>; layoutStyle?: LayoutStyleType; transientDurationSeconds?: number; containerStyle?: ContainerStyleConfig }): Promise<void> {
        const t = this.template();
        if (!t?.id) return;

        const isLayerMode = (event.layers?.length ?? 0) > 0 || event.slots.length === 0 && (event.layers !== undefined);
        const normalizedSlots = isLayerMode ? [] : this.buildSlotsForSave(event.slots);
        const layers = isLayerMode ? (event.layers ?? []) : undefined;
        this.saving.set(true);
        try {
            await firstValueFrom(
                this.templateService.displayTemplateIdStateLayoutPut(t.id, {
                    state: event.state,
                    slots: normalizedSlots.length > 0 ? normalizedSlots : undefined,
                    layers: layers,
                    layoutStyle: event.layoutStyle,
                    containerStyle: event.containerStyle,
                    transientDurationSeconds: event.transientDurationSeconds
                })
            );

            // Fetch the reloaded template directly so slot IDs are available as a local variable
            const reloaded = await firstValueFrom(this.templateService.displayTemplateIdGet(t.id));
            this.template.set(reloaded);

            // Upload any pending image files using the now-resolved slot IDs
            if (event.pendingImages.size > 0) {
                const updatedSlots = reloaded?.stateLayouts?.find(l => l.state === event.state)?.slots ?? [];
                const unresolvedSlotRefs: string[] = [];
                console.log('Saving state layout, pending images:', event.pendingImages);
                for (const [slotRef, file] of event.pendingImages) {
                    const slot = this.findSlotByReference(updatedSlots, slotRef);
                    if (!slot?.id) {
                        unresolvedSlotRefs.push(slotRef);
                        continue;
                    }

                    const request: AddImageToSlotRequest = {
                        fileName: file.name,
                        base64Data: await this.readFileAsBase64(file),
                        description: `${slot.slotId || event.state} image`
                    };
                    await firstValueFrom(
                        this.templateService.displayTemplateDisplayTemplateIdSlotsSlotDbIdImagesPost(t.id, slot.id, request)
                    );
                }

                if (unresolvedSlotRefs.length > 0) {
                    this.snackBar.open(
                        `Layout saved, but ${unresolvedSlotRefs.length} slot image(s) could not be matched: ${unresolvedSlotRefs.join(', ')}`,
                        'Close',
                        { duration: 5000 }
                    );
                }

                await this.loadTemplate();
                if (unresolvedSlotRefs.length === 0) {
                    this.snackBar.open('Layout saved and images uploaded', 'Close', { duration: 2000 });
                }
            } else if ((event.pendingLayerImages?.size ?? 0) > 0) {
                const updatedLayers = reloaded?.stateLayouts?.find(l => l.state === event.state)?.layers ?? [];
                const unresolvedLayerRefs: string[] = [];
                for (const [layerRef, file] of event.pendingLayerImages!) {
                    const layer = this.findLayerByReference(updatedLayers, layerRef);
                    if (!layer?.id) {
                        unresolvedLayerRefs.push(layerRef);
                        continue;
                    }

                    const request: AddImageToLayerRequest = {
                        fileName: file.name,
                        base64Data: await this.readFileAsBase64(file),
                        description: `${layer.layerKey || event.state} image`
                    };
                    await firstValueFrom(
                        this.templateService.displayTemplateDisplayTemplateIdLayersLayerIdImagesPost(t.id, layer.id, request)
                    );
                }

                if (unresolvedLayerRefs.length > 0) {
                    this.snackBar.open(
                        `Layout saved, but ${unresolvedLayerRefs.length} layer image(s) could not be matched: ${unresolvedLayerRefs.join(', ')}`,
                        'Close',
                        { duration: 5000 }
                    );
                }

                await this.loadTemplate();
                if (unresolvedLayerRefs.length === 0) {
                    this.snackBar.open('Layout saved and images uploaded', 'Close', { duration: 2000 });
                }
            } else {
                this.snackBar.open('State layout saved', 'Close', { duration: 2000 });
            }
        } catch (error) {
            var er = error as HttpErrorResponse;
            this.errorMessage.set(er.error || 'Failed to save state layout');
            this.snackBar.open(er.error || 'Failed to save state layout', 'Close', { duration: 3000 });
        } finally {
            this.saving.set(false);
        }
    }

    private buildSlotsForSave(slots: Partial<DisplaySlotDto>[]): DisplaySlotDto[] {
        return slots.map(slot => {
            const isImageType = slot.slotType === DisplaySlotType.Image
                || slot.slotType === DisplaySlotType.ImageWithOverlayText;
            return {
                id: slot.id,
                slotId: slot.slotId?.trim() || null,
                orderNumber: slot.orderNumber ?? undefined,
                slotType: slot.slotType ?? DisplaySlotType.StaticText,
                value: isImageType ? '' : slot.value?.toString() ?? '',
                heightPercent: Number(slot.heightPercent ?? 0),
                fontSize: slot.fontSize ?? null,
                fontColor: slot.fontColor ?? null,
                backgroundColor: slot.backgroundColor ?? null,
                fontWeight: slot.fontWeight ?? null,
                fontFamily: slot.fontFamily ?? null,
                textAlign: slot.textAlign ?? null,
                visible: slot.visible ?? true,
                overlayText: slot.slotType === DisplaySlotType.ImageWithOverlayText
                    ? slot.overlayText ?? null
                    : null
            };
        });
    }

    private findSlotByReference(slots: DisplaySlotDto[], slotRef: string): DisplaySlotDto | undefined {
        const reference = this.parseReference(slotRef);
        const normalizedId = this.normalizeReference(reference.id);
        const normalizedSlotId = this.normalizeReference(reference.slotId ?? slotRef);

        return slots.find(slot =>
            (normalizedId && this.normalizeReference(slot.id) === normalizedId) ||
            (normalizedSlotId && this.normalizeReference(slot.slotId) === normalizedSlotId)
        );
    }

    private findLayerByReference(layers: LayerDefinitionDto[], layerRef: string): LayerDefinitionDto | undefined {
        const reference = this.parseReference(layerRef);
        const normalizedId = this.normalizeReference(reference.id);
        const normalizedLayerKey = this.normalizeReference(reference.layerKey ?? layerRef);

        return layers.find(layer =>
            (normalizedId && this.normalizeReference(layer.id) === normalizedId) ||
            (normalizedLayerKey && this.normalizeReference(layer.layerKey) === normalizedLayerKey)
        );
    }

    private parseReference(reference: string): { id?: string | null; slotId?: string | null; layerKey?: string | null } {
        try {
            const parsed = JSON.parse(reference) as { id?: string | null; slotId?: string | null; layerKey?: string | null };
            if (parsed && typeof parsed === 'object') {
                return parsed;
            }
        } catch {
            // Old plain-string references are still supported.
        }

        return { slotId: reference, layerKey: reference };
    }

    private normalizeReference(value: string | null | undefined): string {
        return value?.trim() ?? '';
    }

    private readFileAsBase64(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onerror = () => reject(new Error('Failed to read file.'));
            reader.onload = () => {
                const result = typeof reader.result === 'string' ? reader.result : '';
                const base64Data = result.includes(',') ? result.split(',')[1] : result;
                if (!base64Data) { reject(new Error('Failed to encode file.')); return; }
                resolve(base64Data);
            };
            reader.readAsDataURL(file);
        });
    }
}
